import { useEffect, useState } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { useTableStore } from '@/stores/tableStore'
import TableForm from '@/components/TableForm'
import QRCodeDisplay from '@/components/QRCodeDisplay'
import type { RestaurantTable } from '@/types'
import { Plus, QrCode, Edit2, Trash2, Power, Users, MapPin, Armchair, Smartphone } from 'lucide-react'

export default function TablesPage() {
  const { user } = useAuthStore()
  const {
    tables,
    isLoading,
    fetchTables,
    toggleTableStatus,
    deleteTable,
  } = useTableStore()

  const [showTableForm, setShowTableForm] = useState(false)
  const [showQRCode, setShowQRCode] = useState(false)
  const [editingTable, setEditingTable] = useState<RestaurantTable | null>(null)
  const [selectedTable, setSelectedTable] = useState<RestaurantTable | null>(null)
  const [showActiveOnly, setShowActiveOnly] = useState(false)

  const restaurantId = user?.restaurantId

  // Load tables on mount
  useEffect(() => {
    if (restaurantId) {
      fetchTables(restaurantId, showActiveOnly)
    }
  }, [restaurantId, showActiveOnly])

  const handleAddTable = () => {
    setEditingTable(null)
    setShowTableForm(true)
  }

  const handleEditTable = (table: RestaurantTable) => {
    setEditingTable(table)
    setShowTableForm(true)
  }

  const handleShowQRCode = (table: RestaurantTable) => {
    setSelectedTable(table)
    setShowQRCode(true)
  }

  const handleToggleStatus = async (table: RestaurantTable) => {
    try {
      await toggleTableStatus(table.id)
    } catch (error) {
      // Error handled by store
    }
  }

  const handleDeleteTable = async (table: RestaurantTable) => {
    if (window.confirm(`Sei sicuro di voler eliminare il tavolo "${table.tableNumber}"?`)) {
      try {
        await deleteTable(table.id)
      } catch (error) {
        // Error handled by store
      }
    }
  }

  const handleCloseForm = () => {
    setShowTableForm(false)
    setEditingTable(null)
  }

  const handleCloseQRCode = () => {
    setShowQRCode(false)
    setSelectedTable(null)
  }

  const activeTables = tables.filter(t => t.active)
  const inactiveTables = tables.filter(t => !t.active)

  if (!restaurantId) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500 dark:text-white/40">Nessun ristorante associato</p>
      </div>
    )
  }

  return (
    <div className="p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Gestione Tavoli</h1>
          <p className="text-gray-500 dark:text-white/40 text-sm mt-1">Configura i tavoli e genera i codici QR</p>
        </div>
        <button
          onClick={handleAddTable}
          className="px-5 py-2.5 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition flex items-center gap-2 font-medium"
        >
          <Plus size={18} />
          Aggiungi Tavolo
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white dark:bg-[#25252d] rounded-2xl border border-gray-200 dark:border-white/[0.08] p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center">
              <Armchair size={20} className="text-blue-500 dark:text-blue-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{tables.length}</div>
              <div className="text-sm text-gray-500 dark:text-white/40">Tavoli totali</div>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-[#25252d] rounded-2xl border border-emerald-200 dark:border-emerald-500/20 p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center">
              <Power size={20} className="text-emerald-500 dark:text-emerald-400" />
            </div>
            <div>
              <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{activeTables.length}</div>
              <div className="text-sm text-gray-500 dark:text-white/40">Tavoli attivi</div>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-[#25252d] rounded-2xl border border-gray-200 dark:border-white/[0.08] p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-white/[0.06] flex items-center justify-center">
              <Power size={20} className="text-gray-400 dark:text-white/30" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-400 dark:text-white/40">{inactiveTables.length}</div>
              <div className="text-sm text-gray-500 dark:text-white/40">Disattivati</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-[#25252d] rounded-xl border border-gray-200 dark:border-white/[0.08] p-4 mb-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showActiveOnly}
            onChange={(e) => setShowActiveOnly(e.target.checked)}
            className="w-4 h-4 text-orange-500 border-gray-300 dark:border-white/20 dark:bg-white/[0.06] rounded focus:ring-orange-500"
          />
          <span className="text-sm font-medium text-gray-700 dark:text-white/70">
            Mostra solo tavoli attivi
          </span>
        </label>
      </div>

      {/* Tables Table */}
      <div className="bg-white dark:bg-[#25252d] rounded-2xl border border-gray-200 dark:border-white/[0.08] overflow-hidden">
        {isLoading && tables.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-orange-500 border-t-transparent mb-3"></div>
            <p className="text-gray-500 dark:text-white/40">Caricamento tavoli...</p>
          </div>
        ) : tables.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-white/[0.06] flex items-center justify-center mx-auto mb-4">
              <Armchair size={32} className="text-gray-400 dark:text-white/30" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Nessun tavolo</h3>
            <p className="text-gray-500 dark:text-white/40 mb-4 text-sm">
              Aggiungi il primo tavolo e genera il codice QR
            </p>
            <button
              onClick={handleAddTable}
              className="inline-flex items-center px-4 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition gap-2 font-medium text-sm"
            >
              <Plus size={16} />
              Aggiungi Tavolo
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-white/[0.06]">
              <thead className="bg-gray-50 dark:bg-white/[0.02]">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-white/40 uppercase tracking-wider">
                    Tavolo
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-white/40 uppercase tracking-wider">
                    Ubicazione
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-white/40 uppercase tracking-wider">
                    Capacità
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-white/40 uppercase tracking-wider">
                    Stato
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 dark:text-white/40 uppercase tracking-wider">
                    QR Code
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 dark:text-white/40 uppercase tracking-wider">
                    Azioni
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-white/[0.06]">
                {tables.map((table) => (
                  <tr
                    key={table.id}
                    className={`${
                      table.active
                        ? 'hover:bg-gray-50 dark:hover:bg-white/[0.02]'
                        : 'bg-gray-50 dark:bg-white/[0.01] opacity-60'
                    } transition-colors`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          table.active
                            ? 'bg-orange-100 dark:bg-orange-500/20'
                            : 'bg-gray-100 dark:bg-white/[0.06]'
                        }`}>
                          <Armchair size={18} className={table.active ? 'text-orange-500 dark:text-orange-400' : 'text-gray-400 dark:text-white/30'} />
                        </div>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">
                          Tavolo {table.tableNumber}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-white/60">
                        {table.location ? (
                          <>
                            <MapPin size={14} className="text-gray-400 dark:text-white/30" />
                            {table.location}
                          </>
                        ) : (
                          <span className="text-gray-400 dark:text-white/30">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-white/60">
                        {table.capacity ? (
                          <>
                            <Users size={14} className="text-gray-400 dark:text-white/30" />
                            {table.capacity} persone
                          </>
                        ) : (
                          <span className="text-gray-400 dark:text-white/30">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-full ${
                        table.active
                          ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400'
                          : 'bg-gray-100 dark:bg-white/[0.06] text-gray-500 dark:text-white/40'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${table.active ? 'bg-emerald-500' : 'bg-gray-400 dark:bg-white/30'}`} />
                        {table.active ? 'Attivo' : 'Disattivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleShowQRCode(table)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-colors"
                      >
                        <Smartphone size={14} />
                        Visualizza QR
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => handleToggleStatus(table)}
                          className={`p-2 rounded-lg transition-colors ${
                            table.active
                              ? 'text-gray-500 dark:text-white/50 hover:bg-gray-100 dark:hover:bg-white/[0.06] hover:text-gray-700 dark:hover:text-white/70'
                              : 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-500/10'
                          }`}
                          title={table.active ? 'Disattiva' : 'Attiva'}
                        >
                          <Power size={16} />
                        </button>
                        <button
                          onClick={() => handleEditTable(table)}
                          className="p-2 rounded-lg text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10 transition-colors"
                          title="Modifica"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteTable(table)}
                          className="p-2 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                          title="Elimina"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      {showTableForm && (
        <TableForm
          table={editingTable}
          restaurantId={restaurantId}
          onClose={handleCloseForm}
        />
      )}

      {showQRCode && selectedTable && (
        <QRCodeDisplay
          table={selectedTable}
          onClose={handleCloseQRCode}
        />
      )}
    </div>
  )
}
